import type { MpPaymentSnapshot, TransactionDetailsPage } from '@mentora/api-public'
import type { ReactNode } from 'react'
import type { PriceFormatter } from '../price-formatter'
import type { TransactionActions } from './transaction-actions-card'
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { RefundHistory } from './refund-history'
import { TransactionActionsCard } from './transaction-actions-card'
import { BuyerCard, PurchaseCard } from './transaction-overview'

// One purchase, for the superadmin: who bought what (`transaction-overview`),
// the money Mentora recorded, the admin's actions and refunds, then — for a
// MercadoPago payment — what MercadoPago reports right now. Figures are shown
// as their source gave them; nothing here is recomputed.

// A free or mock purchase has no provider fee to wait for.
const RAILS = new Set(['stripe', 'mercadopago'])

function Row({ label, children }: { label: string, children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs text-muted-foreground sm:text-sm">{label}</dt>
      <dd className="font-mono text-sm tabular-nums break-all text-foreground sm:text-right">{children}</dd>
    </div>
  )
}

function MercadoPagoSection({ payment }: { payment: MpPaymentSnapshot }) {
  const t = useTranslations('transactions.details')
  const format = useFormatter()
  const money = (amount: number | null) => amount === null
    ? t('absent')
    : payment.currency ? format.number(amount, { style: 'currency', currency: payment.currency }) : String(amount)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('mp.title')}</CardTitle>
        <CardDescription>{t('mp.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="divide-y divide-border/60">
          <Row label={t('mp.status')}>{[payment.status, payment.statusDetail].filter(Boolean).join(' · ') || t('absent')}</Row>
          <Row label={t('mp.amount')}>{money(payment.transactionAmount)}</Row>
          <Row label={t('mp.refunded')}>{money(payment.refundedAmount)}</Row>
          <Row label={t('mp.netReceived')}>{money(payment.netReceivedAmount)}</Row>
        </dl>
        <div>
          <p className="mb-1 text-sm font-medium text-foreground">{t('mp.fees')}</p>
          {payment.fees === null
            ? <p className="text-sm text-muted-foreground">{t('mp.noFeeList')}</p>
            : payment.fees.length === 0
              ? <p className="text-sm text-muted-foreground">{t('mp.noFees')}</p>
              : (
                  <dl className="divide-y divide-border/60">
                    {/* MP fee lines carry no id; their order is MP's and never changes. */}
                    {payment.fees.map((fee, i) => (
                      <Row key={`fee-${i}`} label={`${fee.type ?? t('absent')} · ${fee.feePayer ?? t('absent')}`}>{money(fee.amount)}</Row>
                    ))}
                  </dl>
                )}
        </div>
        <div>
          <p className="mb-1 text-sm font-medium text-foreground">{t('mp.refunds')}</p>
          {payment.refunds.length === 0
            ? <p className="text-sm text-muted-foreground">{t('mp.noRefunds')}</p>
            : (
                <dl className="divide-y divide-border/60">
                  {payment.refunds.map(r => (
                    <Row key={r.id} label={`#${r.id} · ${r.status ?? t('absent')} · ${r.dateCreated ?? t('absent')}`}>{money(r.amount)}</Row>
                  ))}
                </dl>
              )}
        </div>
      </CardContent>
    </Card>
  )
}

export function TransactionDetailsPanel({ page, formatPrice, actions }: {
  page: TransactionDetailsPage
  formatPrice: PriceFormatter
  /** The app's `'use server'` refund and cancel actions. */
  actions: TransactionActions
}) {
  const t = useTranslations('transactions.details')
  const format = useFormatter()
  const { record, provider } = page
  const minor = (cents: number | null, currency: string) => cents === null
    ? t('absent')
    : format.number(cents / 100, { style: 'currency', currency: currency.toUpperCase() })
  const date = (iso: string | null) => iso ? format.dateTime(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' }) : t('absent')

  return (
    <div className="flex flex-col gap-4">
      <PurchaseCard record={record} formatPrice={formatPrice} />
      <BuyerCard buyer={record.buyer} />
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{t('recorded.title')}</CardTitle>
            <Badge variant="outline">{record.provider || t('absent')}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border/60">
            <Row label={t('recorded.paymentId')}>{record.paymentId ?? record.stripeSessionId ?? t('absent')}</Row>
            <Row label={t('recorded.gross')}>{formatPrice(record.grossCents)}</Row>
            <Row label={t('recorded.platform')}>
              {record.platformFeeBps === null || page.platformShareCents === null
                ? t('absent')
                : `${formatPrice(page.platformShareCents)} (${format.number(record.platformFeeBps / 10_000, { style: 'percent', maximumFractionDigits: 2 })})`}
            </Row>
            <Row label={t('recorded.charged')}>
              {record.charged ? minor(record.charged.amountCents, record.charged.currency) : t('absent')}
            </Row>
            {record.settlement
              ? (
                  <>
                    <Row label={t('recorded.landed')}>{minor(record.settlement.amountCents, record.settlement.currency)}</Row>
                    <Row label={t('recorded.fee')}>{minor(record.settlement.feeCents, record.settlement.currency)}</Row>
                    <Row label={t('recorded.net')}>{minor(record.settlement.netCents, record.settlement.currency)}</Row>
                    {record.settlement.exchangeRate && <Row label={t('recorded.exchangeRate')}>{record.settlement.exchangeRate}</Row>}
                    <Row label={t('recorded.settledAt')}>{date(record.settlement.settledAt)}</Row>
                  </>
                )
              : <Row label={t('recorded.fee')}>{RAILS.has(record.provider) && record.grossCents > 0 ? t('recorded.awaitingFee') : t('recorded.noFee')}</Row>}
          </dl>
        </CardContent>
      </Card>

      <TransactionActionsCard
        kind={record.kind}
        id={record.id}
        paymentStatus={record.paymentStatus}
        refundableCents={record.refundableCents}
        currency={record.refundCurrency}
        awaitingRefund={record.refunds.some(r => r.status === 'requested')}
        actions={actions}
      />

      <RefundHistory refunds={record.refunds} retry={actions.retryRefund} />

      {provider.kind === 'mercadopago' && <MercadoPagoSection payment={provider.payment} />}
      {provider.kind === 'unavailable' && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">{t('mp.unavailable')}</CardContent>
        </Card>
      )}
    </div>
  )
}
